import React from "react";
import { PdfTemplateType } from "../document-template.types";
import { PdfTemplateConfiguration } from "../../context/PdfTemplateConfigurationContext";
import { CalculateTotalsResult } from "../../lib/utils/calculate";
import { PDFConfigPlaceholder } from "../../types/enums";
import { ClientEntityType } from "@/lib/supabase/supabaseTypes";
import { replacePdfPlaceholders } from "../utils";

interface PdfTaxAmountTextProps {
	templateType: PdfTemplateType;
	pdfTemplateConfiguration: PdfTemplateConfiguration;
	jobTotals: CalculateTotalsResult;
	generalConfig: PdfTemplateConfiguration["general"];
	client?: ClientEntityType;
	style?: React.CSSProperties;
	currency?: string | null;
}

export const PdfTaxAmountText: React.FC<PdfTaxAmountTextProps> = ({
	templateType,
	pdfTemplateConfiguration,
	jobTotals,
	generalConfig,
	client,
	style = {},
	currency,
}) => {
	const templateConfig = pdfTemplateConfiguration[templateType];
	const showZeroVat = generalConfig?.showZeroVat ?? false;

	if (jobTotals.subjectToDrTaxAmountExclVat.value <= 0 && !showZeroVat) {
		return null;
	}

	const fontSize = {
		sm: "6pt",
		md: "7pt",
		lg: "9pt",
	};
	const fontSizeValue = generalConfig.size
		? fontSize[generalConfig.size]
		: undefined;

	const customTaxAmountText =
		"taxAmountText" in templateConfig
			? templateConfig.taxAmountText
			: undefined;

	// Priority: Client override → Lab standard (PDF config) → Default behavior
	let showZazInclusiveAmounts: boolean;
	if (
		client?.vat_display_override !== null &&
		client?.vat_display_override !== undefined
	) {
		// Use client override: true = inclusive, false = exclusive
		showZazInclusiveAmounts =
			client.vat_display_override == "inclusive" ? true : false;
	} else {
		// Fall back to lab standard (PDF config)
		showZazInclusiveAmounts =
			"showZazInclusiveAmounts" in templateConfig
				? Boolean(templateConfig.showZazInclusiveAmounts)
				: false;
	}

	// Determine which amounts to use based on configuration
	const subjectToTaxAmount = showZazInclusiveAmounts
		? jobTotals.subjectToDrTaxAmountInclVat.text
		: jobTotals.subjectToDrTaxAmountExclVat.text;

	const exemptFromTaxAmount = showZazInclusiveAmounts
		? jobTotals.exemptFromTaxAmountInclVat.text
		: jobTotals.exemptFromTaxAmountExclVat.text;

	// Determine the VAT disclaimer text
	const vatDisclaimer = showZazInclusiveAmounts
		? "inkl. MwSt."
		: "exkl. MwSt.";

	// Use provided currency (should be passed from template using proper hierarchy)
	const effectiveCurrency = currency || "CHF"; // CHF as absolute fallback only

	const taxAmountText =
		(customTaxAmountText
			? replacePdfPlaceholders(customTaxAmountText, {
					[PDFConfigPlaceholder.ZAZTAX]: subjectToTaxAmount,
					[PDFConfigPlaceholder.ZAZFREE]: exemptFromTaxAmount,
					[PDFConfigPlaceholder.ZAZTAXSTATUS]: vatDisclaimer,
					[PDFConfigPlaceholder.CURRENCY]: effectiveCurrency,
				})
			: "") ||
		`* Der steuerpflichtige Betrag ergibt ${effectiveCurrency} ${subjectToTaxAmount}, steuerfrei sind ${effectiveCurrency} ${exemptFromTaxAmount}. (Angaben ${vatDisclaimer} und ohne Gewähr)`;

	return (
		<div
			aria-label="Steuerpflichtiger Betrag"
			style={{
				fontSize: fontSizeValue,
				paddingTop: "2mm",
				...style,
			}}
		>
			{taxAmountText}
		</div>
	);
};
